import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bug, ExternalLink, MoreVertical, CheckCircle, EyeOff, RotateCcw, Server, Smartphone, Globe } from 'lucide-react'
import { sentryApi } from '../services/api'
import { showError } from '../utils/showError'
import { formatTimeAgo } from '../utils/formatters'
import { levelColors, projectColors, statusColors } from '../constants/sentry'
import {
  PageHeader,
  SearchBar,
  ListContainer,
  LoadingSpinner,
  EmptyState,
  Badge,
  Card,
  CardBody,
  FormSelect,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '../components/common'

const STORAGE_KEY = 'sentryIssuesFilters'

const statusOptions = [
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
  { value: '', label: 'All' },
]

const timePeriods = [
  { value: '', label: 'All Time' },
  { value: '1h', label: 'Last 1 Hour' },
  { value: '4h', label: 'Last 4 Hours' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '14d', label: 'Last 14 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
]

const sortOptions = [
  { value: 'date', label: 'Last Seen' },
  { value: 'new', label: 'First Seen' },
  { value: 'freq', label: 'Events' },
  { value: 'user', label: 'Users' },
  { value: 'priority', label: 'Priority' },
]

const projects = [
  { slug: 'reziland-api', label: 'API', icon: Server, activeClass: 'bg-blue-100 text-blue-700 border-blue-300' },
  { slug: 'reziland-mobile', label: 'Mobile', icon: Smartphone, activeClass: 'bg-purple-100 text-purple-700 border-purple-300' },
  { slug: 'reziland-portal', label: 'Portal', icon: Globe, activeClass: 'bg-green-100 text-green-700 border-green-300' },
]

const getTimeCutoff = (period) => {
  if (!period) return null
  const match = period.match(/^(\d+)([hd])$/)
  if (!match) return null
  const [, amount, unit] = match
  const ms = unit === 'h' ? amount * 3600000 : amount * 86400000
  return new Date(Date.now() - ms)
}

const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore */ }
  return {}
}

const SentryIssues = () => {
  const navigate = useNavigate()
  const saved = useMemo(() => loadSavedFilters(), [])

  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [projectFilter, setProjectFilter] = useState(saved.projectFilter ?? '')
  const [statusFilter, setStatusFilter] = useState(saved.statusFilter ?? 'unresolved')
  const [timePeriod, setTimePeriod] = useState(saved.timePeriod ?? '')
  const [sortBy, setSortBy] = useState(saved.sortBy ?? 'date')
  const [environmentFilter, setEnvironmentFilter] = useState(saved.environmentFilter ?? '')
  const [openMenuId, setOpenMenuId] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(null)

  // Persist filters
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      projectFilter, statusFilter, timePeriod, sortBy, environmentFilter,
    }))
  }, [projectFilter, statusFilter, timePeriod, sortBy, environmentFilter])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [searchInput, searchTerm])

  useEffect(() => {
    loadIssues()
  }, [projectFilter, statusFilter, sortBy, environmentFilter])

  const loadIssues = async () => {
    try {
      setLoading(true)
      const params = { sort: sortBy }
      if (projectFilter) params.projectSlug = projectFilter
      if (environmentFilter) params.environment = environmentFilter

      const query = statusFilter ? `is:${statusFilter}` : ''
      if (query) params.query = query

      const response = await sentryApi.getIssues(params)
      setIssues(response.data || [])
    } catch (error) {
      console.error('Error loading Sentry issues:', error)
      setIssues([])
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (e, issueId, newStatus) => {
    e.stopPropagation()
    setOpenMenuId(null)
    try {
      setUpdatingStatus(issueId)
      await sentryApi.updateStatus(issueId, newStatus)
      await loadIssues()
    } catch (error) {
      console.error('Error updating status:', error)
      showError(error)
    } finally {
      setUpdatingStatus(null)
    }
  }

  const toggleProject = useCallback((slug) => {
    setProjectFilter(prev => prev === slug ? '' : slug)
  }, [])

  const toggleEnvironment = useCallback((env) => {
    setEnvironmentFilter(prev => prev === env ? '' : env)
  }, [])

  const filteredIssues = useMemo(() => {
    let result = issues

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(issue =>
        issue.title?.toLowerCase().includes(term) ||
        issue.culprit?.toLowerCase().includes(term) ||
        issue.shortId?.toLowerCase().includes(term)
      )
    }

    const cutoff = getTimeCutoff(timePeriod)
    if (cutoff) {
      result = result.filter(issue =>
        issue.lastSeen && new Date(issue.lastSeen) >= cutoff
      )
    }

    return result
  }, [issues, searchTerm, timePeriod])

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sentry Issues"
        subtitle={`${filteredIssues.length} issues`}
      />

      <Card>
        <CardBody>
          <div className="space-y-3">
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search issues by title, culprit, or ID..."
            />
            <div className="flex flex-wrap items-center gap-3">
              <FormSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={statusOptions}
              />
              <FormSelect
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                options={timePeriods}
              />

              {/* Project toggle buttons */}
              <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
                {projects.map((p) => {
                  const Icon = p.icon
                  const active = projectFilter === p.slug
                  return (
                    <button
                      key={p.slug}
                      onClick={() => toggleProject(p.slug)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-r border-gray-200 last:border-r-0 transition-colors ${
                        active ? p.activeClass : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                      title={active ? `Showing ${p.label} only (click to show all)` : `Filter by ${p.label}`}
                    >
                      <Icon size={14} />
                      {p.label}
                    </button>
                  )
                })}
              </div>

              {/* Environment toggle buttons */}
              <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
                {['develop', 'production'].map((env) => {
                  const active = environmentFilter === env
                  return (
                    <button
                      key={env}
                      onClick={() => toggleEnvironment(env)}
                      className={`px-3 py-1.5 text-xs font-medium border-r border-gray-200 last:border-r-0 transition-colors ${
                        active
                          ? 'bg-primary-100 text-primary-700 border-primary-300'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                      title={active ? `Showing ${env} only (click to show all)` : `Filter by ${env}`}
                    >
                      {env}
                    </button>
                  )
                })}
              </div>

              <FormSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={sortOptions}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <ListContainer>
        {filteredIssues.length === 0 ? (
          <EmptyState
            icon={Bug}
            title="No Sentry issues found"
            description="No issues were found for the selected filters."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell header>Project</TableCell>
                <TableCell header>Issue</TableCell>
                <TableCell header>Level</TableCell>
                <TableCell header>Events</TableCell>
                <TableCell header>Users</TableCell>
                <TableCell header>First Seen</TableCell>
                <TableCell header>Last Seen</TableCell>
                <TableCell header className="w-16">Actions</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredIssues.map((issue) => (
                <TableRow
                  key={issue.issueId}
                  clickable
                  onClick={() => navigate(`/sentry-issues/${issue.issueId}`)}
                >
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${projectColors[issue.projectName] || 'bg-gray-100 text-gray-700'}`}>
                      {issue.projectName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-md">
                      <div className="text-[0.7rem] text-gray-400 font-mono">{issue.shortId}</div>
                      <div className="text-sm font-medium text-gray-900 truncate">{issue.title}</div>
                      {issue.culprit && (
                        <div className="text-xs text-gray-500 truncate">{issue.culprit}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={levelColors[issue.level] || 'default'} size="small">
                      {issue.level}
                    </Badge>
                  </TableCell>
                  <TableCell>{issue.eventCount?.toLocaleString()}</TableCell>
                  <TableCell>{issue.userCount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{formatTimeAgo(issue.firstSeen)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{formatTimeAgo(issue.lastSeen)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="relative flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {issue.permalink && (
                        <a
                          href={issue.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                          title="View on Sentry"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenMenuId(openMenuId === issue.issueId ? null : issue.issueId)
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                        disabled={updatingStatus === issue.issueId}
                      >
                        <MoreVertical size={14} />
                      </button>
                      {openMenuId === issue.issueId && (
                        <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                          {issue.status !== 'resolved' && (
                            <button
                              onClick={(e) => handleStatusUpdate(e, issue.issueId, 'resolved')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <CheckCircle size={14} className="text-green-500" />
                              Resolve
                            </button>
                          )}
                          {issue.status !== 'ignored' && (
                            <button
                              onClick={(e) => handleStatusUpdate(e, issue.issueId, 'ignored')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <EyeOff size={14} className="text-gray-500" />
                              Ignore
                            </button>
                          )}
                          {issue.status !== 'unresolved' && (
                            <button
                              onClick={(e) => handleStatusUpdate(e, issue.issueId, 'unresolved')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <RotateCcw size={14} className="text-orange-500" />
                              Unresolve
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListContainer>
    </div>
  )
}

export default SentryIssues
